import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Search } from 'lucide-react'
import { toast } from 'sonner'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useDeleteLead, useGetLeadsByInvestigation } from '@/application/services/lead.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { CreateLeadDialog } from '@/ui/components/leads/CreateLeadDialog'
import { LeadTile } from './LeadTile'
import { SortableLeadTile } from './SortableLeadTile'
import { useLeadReorder } from './useLeadReorder'

/**
 * Leads section: H2 title with count badge, add-lead button and dialog,
 * full lead list and delete-lead handling. Does not own investigation
 * metadata, team management or notes — those live in the page shell.
 */
export function LeadsSection({ investigationId }: { investigationId: string }) {
  const { t } = useTranslation()
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const { data: leads, isLoading: leadsLoading } = useGetLeadsByInvestigation(investigationId)
  const { mutate: deleteLead } = useDeleteLead()
  const { user } = useAuthStore()

  const handleDelete = (event: React.MouseEvent, leadId: string) => {
    event.preventDefault()
    event.stopPropagation()
    if (window.confirm(t('admin.leads_delete_confirm'))) {
      deleteLead(leadId, {
        onSuccess: () => toast.success(t('admin.leads_deleted')),
        onError: (error: unknown) =>
          toast.error(
            t('admin.leads_delete_failed', {
              error: error instanceof Error ? error.message : String(error),
            }),
          ),
      })
    }
  }

  // Leads already come back ordered by `position` from the API — no client-side
  // re-sorting, so the manual order set via drag & drop (or the API) is honoured as-is.
  const sortedLeads = leads ?? []

  const canReorder = user?.role === 'streamer' || user?.role === 'admin'

  const { orderedLeads, handleDragEnd } = useLeadReorder({
    investigationId,
    leads: sortedLeads,
    onSuccess: () => toast.success(t('investigation_detail.lead_reorder_success')),
    onError: (message) =>
      toast.error(t('investigation_detail.lead_reorder_failed', { error: message })),
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  return (
    <>
      {/* ─── Leads title ─── */}
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-bold flex items-center gap-3">
          {t('investigation_detail.leads')}
          <Badge variant="secondary">{leadsLoading ? '…' : (leads?.length ?? 0)}</Badge>
        </h2>
        <Button
          size="sm"
          onClick={() => setAddDialogOpen(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          {t('admin.inv_leads_add')}
        </Button>
      </div>

      {/* ─── Leads list ─── */}
      {leadsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_item, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg overflow-hidden flex h-28"
            >
              <Skeleton className="w-28 sm:w-40 shrink-0" />
              <div className="flex-1 p-4 space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : !leads || leads.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl text-muted-foreground">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">{t('admin.inv_leads_empty_title')}</p>
          <p className="text-sm mt-1">{t('admin.inv_leads_empty_body')}</p>
          <Button
            size="sm"
            className="mt-4"
            onClick={() => setAddDialogOpen(true)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            {t('admin.inv_leads_add')}
          </Button>
        </div>
      ) : canReorder ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedLeads.map((lead) => lead.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {orderedLeads.map((lead) => (
                <SortableLeadTile
                  key={lead.id}
                  lead={lead}
                  investigationId={investigationId}
                  onDelete={(event) => handleDelete(event, lead.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {orderedLeads.map((lead) => (
            <LeadTile
              key={lead.id}
              lead={lead}
              investigationId={investigationId}
              onDelete={(event) => handleDelete(event, lead.id)}
            />
          ))}
        </div>
      )}

      <CreateLeadDialog
        investigationId={investigationId}
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        descriptionRequired={false}
        showImageField
      />
    </>
  )
}
